/**
 * Разбор ответа `POST eplex/temp_cart/make_order`.
 *
 * Бэк отвечает HTTP 200 даже когда заказ не создан: результат лежит во вложенном
 * конверте Tarantool `data`:
 *
 *   { "status": "ok",  "items": { "<thash>": { "status": 1 } } }               // всё прошло
 *   { "status": "ok",  "items": { "<thash>": { "status": -1, "error": "…" } } } // отказ по позиции
 *   { "status": "ok",  "items": { "<thash>": { "status": -3 } } }               // отказ без текста
 *   { "status": "err", "err": "Недостаточно средств", "error_code": 402 }       // отказ по всей операции
 *
 * Ключи `items` — хеши запрошенных позиций, но записаны они иначе, чем в
 * запросе: префикс временной корзины `t` в ответе теряется (запрос уходит с
 * `t1ed26596…`, ответ приходит с `1ed26596…`). Поэтому сопоставляем по хешу без
 * префикса, а не «ключ в ключ» (CAB-683).
 *
 * Отказ по позиции кодируется **любым отрицательным** статусом, а не только −1:
 * −3 приходит без текста ошибки вовсе (CAB-602 п.2) — по SER-1295 так выглядит
 * отказ биллинга при нулевом балансе токенов, текст которого бэк наружу не несёт.
 *
 * Отказ по всей операции раньше не проверялся вообще. Хуже ложной ошибки только
 * ложный успех: пользователь считает, что купил, и узнаёт правду от поставщика.
 * Поэтому конверт разбираем явно и целиком.
 */

interface MakeOrderItemPayload {
  status?: number | string;
  error?: unknown;
}

interface MakeOrderPayload {
  status?: unknown;
  err?: unknown;
  error?: unknown;
  confirm?: unknown;
  confirm_text?: unknown;
  items?: Record<string, MakeOrderItemPayload>;
}

/**
 * Бэк просит подтвердить повтор (CAB-683): прошлая попытка по позиции упала на
 * стороне поставщика (5xx/6xx), и заказ мог фактически уйти. Оформление при этом
 * не запущено ни по одной позиции запроса — его повторяют тем же телом с
 * `confirmed: true` после согласия пользователя.
 */
export interface MakeOrderConfirmRequest {
  /** Текст вопроса от бэка (`confirm_text`); пустая строка — текста не прислали. */
  text: string;
}

/** Статус одной позиции из ответа. */
export interface MakeOrderItemStatus {
  status: number;
  /** Текст от бэка; пустая строка — причину не назвали (типично для −3). */
  error: string;
}

/** Отказ по конкретной позиции корзины. */
export interface RejectedOrderItem extends MakeOrderItemStatus {
  thash: string;
}

export interface MakeOrderResult {
  /** Заказ создан по всем запрошенным позициям. */
  ok: boolean;
  /**
   * Ошибка всей операции (конверт `status: "err"`): ни одна позиция не заказана.
   * Текст — от бэка, он объясняет причину лучше любого общего фоллбека.
   */
  error: string | null;
  /** Позиции, отклонённые поштучно (отрицательный статус). */
  rejected: RejectedOrderItem[];
  /** Все статусы позиций из ответа — по ключам, которыми их назвал бэк. */
  statuses: Record<string, MakeOrderItemStatus>;
  /** Бэк ждёт подтверждения повтора; null — обычный ответ. */
  confirm: MakeOrderConfirmRequest | null;
}

/**
 * Префикс временной корзины в ключе позиции. В журнале позиция названа с ним
 * (`thash` = `hash_key` = `t` + 32 hex), запрос уходит с ним же, а в ответе бэк
 * присылает голый хеш. Сопоставление «ключ в ключ» из-за этого не находило
 * принятую позицию: она считалась неподтверждённой, «оформляется» снималось со
 * строки сразу после ответа, и статус возвращался к корзинному (CAB-683).
 * Хеш шестнадцатеричный, буквы `t` в нём быть не может, поэтому ведущая `t` —
 * всегда префикс, а не часть хеша.
 */
const TEMP_CART_KEY_PREFIX = "t";

/** Хеш позиции без префикса временной корзины — общий знаменатель запроса и ответа. */
export function normalizeOrderItemKey(key: string): string {
  return key.startsWith(TEMP_CART_KEY_PREFIX) ? key.slice(TEMP_CART_KEY_PREFIX.length) : key;
}

function readErrorText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Разбирает тело ответа make_order (весь `response.data`) в решение «успех или нет».
 * Отсутствующий/неожиданный конверт считаем успехом: так себя вёл фронт до
 * появления биллинга, и ломать оформление из-за неизвестной формы ответа хуже,
 * чем довериться HTTP 200 — явные признаки отказа мы при этом не пропустим.
 */
export function parseMakeOrderResult(responseData: unknown): MakeOrderResult {
  const payload = (responseData as { data?: MakeOrderPayload } | null | undefined)?.data;

  const statuses: Record<string, MakeOrderItemStatus> = {};
  const rejected: RejectedOrderItem[] = [];
  for (const [thash, item] of Object.entries(payload?.items ?? {})) {
    const status = Number(item?.status);
    if (!Number.isFinite(status)) continue;
    const error = readErrorText(item?.error) ?? "";
    statuses[thash] = { status, error };
    if (status < 0) rejected.push({ thash, status, error });
  }

  // «err» (или любой иной статус, кроме «ok») — отказ по всей операции.
  const envelopeFailed = typeof payload?.status === "string" && payload.status !== "ok";
  const error = envelopeFailed ? (readErrorText(payload?.err) ?? readErrorText(payload?.error)) : null;

  // Запрос подтверждения — тоже не успех: заказ не запущен, решение за пользователем.
  const confirm: MakeOrderConfirmRequest | null =
    !envelopeFailed && payload?.confirm === true ? { text: readErrorText(payload.confirm_text) ?? "" } : null;

  return {
    ok: !envelopeFailed && confirm === null && rejected.length === 0,
    error: envelopeFailed ? error : null,
    rejected,
    statuses,
    confirm,
  };
}

/** Как ответ бэка распределил запрошенные позиции. */
export interface OrderPlacementOutcome<T> {
  /** Приём подтверждён ответом: ждём смену статуса в журнале. */
  placed: T[];
  /** Отказ по позиции: заказа нет, причину нужно показать на строке. */
  rejected: Array<{ item: T; status: number; error: string }>;
  /** Ответ об этих позициях ничего не сказал — «оформляется» показывать нечестно. */
  unconfirmed: T[];
}

/**
 * Сопоставляет отправленные позиции с ответом бэка **по хешу позиции**.
 *
 * Так «оформляется» получают только те позиции, приём которых бэк подтвердил их
 * же хешем. Раньше отказ вычислялся вычитанием списков, и любое расхождение
 * ключей ответа с ключами запроса приводило к худшему из исходов: отклонённая
 * позиция помечалась как оформляющаяся, спиннер в колонке статуса крутился до
 * таймаута, а строка так и не показывала отказ (CAB-602 п.2).
 *
 * Сравниваем хеши без префикса временной корзины: буквальное совпадение ключей
 * недостижимо — бэк отвечает по позиции голым хешем (см. normalizeOrderItemKey).
 *
 * Позиция без своего ключа в ответе остаётся `unconfirmed`: отметку не ставим
 * вовсе. Это не теряет прогресс — при реальном оформлении бэк сам переводит
 * позицию в статус 1, и спиннер приходит уже из данных строки.
 */
export function resolveOrderPlacement<T extends { thash: string }>(
  items: T[],
  result: MakeOrderResult,
): OrderPlacementOutcome<T> {
  const outcome: OrderPlacementOutcome<T> = { placed: [], rejected: [], unconfirmed: [] };
  const reportedByHash = new Map<string, MakeOrderItemStatus>();
  for (const [key, status] of Object.entries(result.statuses)) {
    reportedByHash.set(normalizeOrderItemKey(key), status);
  }

  // отказ всей операции или запрос подтверждения — не заказано ничего,
  // независимо от статусов позиций
  if (result.error !== null || result.confirm !== null) {
    outcome.unconfirmed.push(...items);
    return outcome;
  }

  for (const item of items) {
    const reported = result.statuses[item.thash] ?? reportedByHash.get(normalizeOrderItemKey(item.thash));
    if (!reported) {
      outcome.unconfirmed.push(item);
    } else if (reported.status < 0) {
      outcome.rejected.push({ item, status: reported.status, error: reported.error });
    } else {
      outcome.placed.push(item);
    }
  }

  return outcome;
}
